import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/config/company";

/**
 * SEOHead Component - Centralized SEO meta tag management
 * 
 * Provides consistent meta tag generation with validation and truncation
 * to meet SEO best practices. Integrates with React Helmet Async for
 * server-side rendering and client-side navigation.
 * 
 * Features:
 * - Title validation and truncation (max 60 chars)
 * - Description validation and truncation (70-155 chars)
 * - Automatic canonical URL generation
 * - Open Graph and Twitter Card support
 * - JSON-LD structured data injection
 * - Development mode validation warnings
 */

export interface SEOHeadProps {
  /** Page title (max 60 chars, will be truncated with warning) */
  title: string;
  /** Meta description (70-155 chars, will be truncated with warning) */
  description: string;
  /** Relative path for canonical URL (e.g., "/treatments/dental/") */
  canonicalPath: string;
  /** Open Graph image URL (absolute or relative) */
  ogImage?: string;
  /** Open Graph type */
  ogType?: 'website' | 'article';
  /** Prevent search engine indexing */
  noindex?: boolean;
  /** JSON-LD structured data object */
  structuredData?: object;
  /** Keywords for meta keywords tag (optional) */
  keywords?: string;
  /** Twitter card type */
  twitterCard?: 'summary' | 'summary_large_image';
}

/**
 * Validates and truncates title to max 60 characters
 * Logs warning in development if truncation occurs
 */
function validateTitle(title: string): string {
  if (!title || title.trim().length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SEOHead] Empty title provided, using fallback');
    }
    return 'TrustedHeal | Medical Tourism India';
  }

  const trimmed = title.trim();
  
  if (trimmed.length > 60) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[SEOHead] Title exceeds 60 characters (${trimmed.length} chars): "${trimmed}"\n` +
        `Truncated to: "${trimmed.substring(0, 60)}"`
      );
    }
    return trimmed.substring(0, 60);
  }

  return trimmed;
}

/**
 * Validates and truncates description to 70-155 characters
 * Logs warning in development if out of range
 */
function validateDescription(description: string): string {
  if (!description || description.trim().length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SEOHead] Empty description provided, using fallback');
    }
    return 'Discover world-class, cost-effective medical treatments in India with TrustedHeal. Access JCI-accredited hospitals and save up to 70%.';
  }

  const trimmed = description.trim();
  
  if (trimmed.length < 70) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[SEOHead] Description too short (${trimmed.length} chars, minimum 70): "${trimmed}"`
      );
    }
  }

  if (trimmed.length > 155) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[SEOHead] Description exceeds 155 characters (${trimmed.length} chars): "${trimmed}"\n` +
        `Truncated to: "${trimmed.substring(0, 155)}"`
      );
    }
    return trimmed.substring(0, 155);
  }

  return trimmed;
}

/**
 * Generates absolute HTTPS canonical URL from relative path
 * Ensures proper trailing slash and HTTPS protocol
 */
function generateCanonicalURL(relativePath: string): string {
  if (!relativePath) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SEOHead] Empty canonical path provided, using homepage');
    }
    return COMPANY.website + '/';
  }

  // Ensure path starts with /
  const normalizedPath = relativePath.startsWith('/') ? relativePath : '/' + relativePath;
  
  // Ensure path ends with / for consistency
  const pathWithSlash = normalizedPath.endsWith('/') ? normalizedPath : normalizedPath + '/';
  
  return COMPANY.website + pathWithSlash;
}

/**
 * Generates absolute URL for Open Graph image
 */
function generateImageURL(image?: string): string {
  if (!image) {
    return `${COMPANY.website}/og-default.jpg`;
  }

  // If already absolute URL, return as-is
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }

  // Convert relative path to absolute
  const normalizedPath = image.startsWith('/') ? image : '/' + image;
  return COMPANY.website + normalizedPath;
}

/**
 * SEOHead Component
 * 
 * Centralized component for managing all SEO-related meta tags.
 * Validates input, generates fallbacks, and ensures consistent
 * SEO implementation across all pages.
 * 
 * @example
 * ```tsx
 * <SEOHead
 *   title="Dental Implants in India - Save 70%"
 *   description="Get affordable dental implants in India at JCI-accredited hospitals."
 *   canonicalPath="/treatments/dental/dental-implants/"
 *   ogImage="/images/dental-implants-og.jpg"
 *   ogType="article"
 * />
 * ```
 */
export function SEOHead({
  title,
  description,
  canonicalPath,
  ogImage,
  ogType = 'website',
  noindex = false,
  structuredData,
  keywords,
  twitterCard = 'summary_large_image'
}: SEOHeadProps) {
  // Validate and normalize inputs
  const validatedTitle = validateTitle(title);
  const validatedDescription = validateDescription(description);
  const canonicalURL = generateCanonicalURL(canonicalPath);
  const imageURL = generateImageURL(ogImage);

  // Use validated title/description for OG tags (or allow override)
  const ogTitle = validatedTitle;
  const ogDescription = validatedDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{validatedTitle}</title>
      <meta name="title" content={validatedTitle} />
      <meta name="description" content={validatedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={COMPANY.name} />
      
      {/* Robots Meta Tag */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalURL} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalURL} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={imageURL} />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={canonicalURL} />
      <meta property="twitter:title" content={ogTitle} />
      <meta property="twitter:description" content={ogDescription} />
      <meta property="twitter:image" content={imageURL} />
      <meta property="twitter:site" content="@TrustedHeal" />

      {/* JSON-LD Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
